import { CTALayer } from "@manager/layer/CTALayer";
import { CachedCTAResource } from "@manager/loader/scenarioLoader/CachedCTAResource";
import React from "react";
import { CTAHandle } from "../../api/cta/ctaFunction/CTAHandle";
import { Dimensions } from "../../utils/dimensions";
import { WindowResizeHelper } from "../../utils/WindowResizeHelper";
import DOMPurify from "dompurify";

import "@styles/c-ctaLayer.scss";

interface Props {
  className?: string;
}

interface State {
  aspectRatio?: number;
  width: number;
  height: number;
}

interface InitializedCTA {
  ctaElement: HTMLElement;
  handle: CTAHandle;
}

export class CTALayerComponent extends React.Component<Props, State> implements CTALayer {
  private static DATA_HASH_CODE_ATTR = "data-hash-code";

  private layer: HTMLElement;
  private container: HTMLElement;

  private reusableCTAs: { [hashCode: number]: InitializedCTA } = {};
  private ctaHandles: { [id: number]: CTAHandle } = {};

  constructor(props: Props) {
    super(props);
    this.state = {
      width: 0,
      height: 0,
    };
  }

  render() {
    return (
      <div className={`${this.props.className || ""} c-ctaLayer`} ref={(elmt) => (this.layer = elmt)}>
        <div
          className="c-ctaLayer__container"
          ref={(elmt) => (this.container = elmt)}
          style={{
            width: `${this.state.width}px`,
            height: `${this.state.height}px`,
          }}
        />
      </div>
    );
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleWindowResize);
  }

  // start of CTALayer implementation
  setAspectRatio(aspectRatio: number) {
    this.setState({
      aspectRatio,
      ...Dimensions.calculateDimensions(aspectRatio, this.layer.clientWidth, this.layer.clientHeight),
    });
  }

  loadCTA(hashCode: number, isReusable: boolean, ctaId: number, ctaResource: CachedCTAResource, ctaCustomAttributes: any): void {
    const existingCTA = this.reusableCTAs[hashCode];
    if (existingCTA) {
      this.container.appendChild(existingCTA.ctaElement);
      this.ctaHandles[ctaId] = existingCTA.handle;
    } else {
      const ctaElement = this.createCTAElement(hashCode, ctaResource.ctaHtml);
      this.container.appendChild(ctaElement);

      const handle = ctaResource.ctaFunction(ctaId, ctaElement, ctaCustomAttributes);
      this.ctaHandles[ctaId] = handle;

      if (isReusable) {
        this.reusableCTAs[hashCode] = { ctaElement, handle };
      }
    }
  }

  removeCTA(ctaId: number, hashCode: number): void {
    delete this.ctaHandles[ctaId];

    const ctaBoard = this.container && this.container.querySelector(`[${CTALayerComponent.DATA_HASH_CODE_ATTR}="${hashCode}"]`);
    ctaBoard && ctaBoard.parentNode.removeChild(ctaBoard);
  }

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    const handle = this.ctaHandles[ctaId];
    handle && handle.action(actionId, args);
  }
  // end of CTALayer implementation

  private createCTAElement(hashCode: number, ctaHTML: string): HTMLElement {
    const element = document.createElement("div");
    element.setAttribute(CTALayerComponent.DATA_HASH_CODE_ATTR, `${hashCode}`);
    element.setAttribute("class", "c-ctaLayer__element");
    element.innerHTML = DOMPurify.sanitize(ctaHTML);
    return element;
  }

  private recalculateDimensions() {
    if (this.layer && this.state.aspectRatio) {
      this.setState({
        ...Dimensions.calculateDimensions(this.state.aspectRatio, this.layer.clientWidth, this.layer.clientHeight),
      });
    }
  }

  private handleWindowResize = () => {
    WindowResizeHelper.doOnResize(() => this.recalculateDimensions());
  };
}
